import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useLazyQuery } from "@apollo/client";
import { GET_AUTHED_CLIENT_QUERY } from "../../graphql/queries/clients";
import { GET_PACKAGES_QUERY } from "../../graphql/queries/packages";
import { GET_CLIENT_MINIMUM_PAYMENT } from "../../graphql/queries/payments";
import Loader from "../../components/loader/loader";
import { formatDateString } from "../../utils/formatDateString";
import { formatCurrencyInRands } from "../../utils/randFomatter";
import Button from "../../components/Button/Button";
import Modal from "../../components/modal/Modal";
import BasicModal from "../../components/basic-modal/BasicModal";
import AddPayment from "../../components/add-payment/add-payment";
import AddVoucherPayment from "../../components/add-voucher-payment/add-voucher-payment";
import AddOneVoucherPayment from "../../components/add-one-voucher-payment/add-one-voucher-payment";
import SingleSelect from "../../components/single-select/single-select";
import zonkeEnergyTCs from "../../utils/zonkeTCs";

const TopUp = () => {
  const clientResponse = useQuery(GET_AUTHED_CLIENT_QUERY, {
    fetchPolicy: "network-only",
  });

  const packagesResponse = useQuery(GET_PACKAGES_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const [getMinimumPayment, { data: minimumData }] = useLazyQuery(
    GET_CLIENT_MINIMUM_PAYMENT
  );

  // Clients who picked Bank App / Ozow on the landing page have already chosen
  // how they want to pay, so open that modal for them instead of asking again.
  const location = useLocation();
  const navigate = useNavigate();
  const [payByBankModal, setPayByBankModal] = useState(
    Boolean(location.state?.openPayByBank)
  );
  // Kept after the flag below is cleared, so the Ozow result still knows to go
  // back to the landing page.
  const [startedOnLanding] = useState(Boolean(location.state?.openPayByBank));
  const [payByVoucherModal, setPayByVoucherModal] = useState(false);
  const [payByOneVoucherModal, setPayByOneVoucherModal] = useState(false);
  const [showTermsAndConditions, setShowTermsAndConditions] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [requiredBalance, setRequiredBalance] = useState(0);
  const [daysProvided, setDaysProvided] = useState(0);
  const [bsgAvailable, setBsgAvailable] = useState(0);
  const [packageDatils, setPackageDetails] = useState(null);

  // Clear the flag so the modal does not come back on a refresh or a back press.
  useEffect(() => {
    if (location.state?.openPayByBank) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  const onPayByBankClose = () => setPayByBankModal(false);
  const onPayByVoucherClose = () => setPayByVoucherModal(false);
  const onPayByOneVoucherClose = () => setPayByOneVoucherModal(false);
  const onTermsAndConditions = () => setShowTermsAndConditions(true);
  const onTermsAndConditionsClose = () => setShowTermsAndConditions(false);

  const client = clientResponse.data?.authenticatedClient;
  const packages = packagesResponse.data?.packages?.items || [];

  useEffect(() => {
    if (client && packages.length) {
      const clientPkg = packages.find((p) => p.id === client.package.id);
      if (clientPkg) {
        const selected = {
          label: clientPkg.name,
          value: clientPkg.id,
          price: clientPkg.price,
        };
        setSelectedPackage(selected);
        fetchMinimumPayment(selected);
      }
    }
  }, [client, packages]);

  useEffect(() => {
    if (minimumData?.clientMinimumPayment) {
      setRequiredBalance(minimumData.clientMinimumPayment.requiredBalance);
      setDaysProvided(minimumData.clientMinimumPayment.daysProvided);
      setBsgAvailable(minimumData.clientMinimumPayment.bsgAvailable);
    }
  }, [minimumData]);

  const fetchMinimumPayment = (pkg) => {
    if (pkg?.value && client?.clientId) {
      getMinimumPayment({
        variables: {
          clientId: parseInt(client.clientId, 10),
          packageId: parseInt(pkg.value, 10),
        },
      });
    }
  };

  const onPackageChange = (pkg) => {
    setSelectedPackage(pkg);
    fetchMinimumPayment(pkg);
  };

  const packageOptions = packages.map((p) => ({
    label: p.name,
    value: p.id,
    price: p.price,
    billingDays: p.billingDays,
  }));

  useEffect(() => {
    const clientPackage = client?.package?.id;
    let defaultPackage = packageOptions.find((p) => p.value == clientPackage);

    if (selectedPackage) {
      defaultPackage = packageOptions.find(
        (p) => p.value == selectedPackage.value
      );
    }

    setPackageDetails(defaultPackage);
  }, [selectedPackage, selectedPackage]);

  if (clientResponse.loading || packagesResponse.loading) {
    return <Loader sizeClass={"size--medium"} />;
  }

  if (clientResponse.error || packagesResponse.error) {
    return <div className="display-page__error">Something went wrong</div>;
  }

  const paragraph = client.isExpired
    ? `Hi ${client.name}! Your energy credit has expired. Please make a payment to re-enable your Zonke Energy service.`
    : `Hello ${client.name}! Please make a payment before ${formatDateString(
        client.expiryDate
      )} to avoid any gaps in your Zonke Energy service.`;

  return (
    <div className="display-page">
      <Modal
        modal={showTermsAndConditions}
        onClose={onTermsAndConditionsClose}
        modalSize="large"
      >
        <BasicModal
          title={""}
          body={zonkeEnergyTCs}
          onCancel={onTermsAndConditionsClose}
          isHtml={true}
        />
      </Modal>

      <Modal
        modal={payByBankModal}
        onClose={onPayByBankClose}
        modalSize="large"
      >
        <AddPayment
          title={"Add Payment"}
          body={"Pay through your bank account"}
          onCancel={onPayByBankClose}
          clientId={client.clientId}
          defaultPackage={selectedPackage}
          returnTo={startedOnLanding ? "/" : undefined}
        />
      </Modal>

      <Modal
        modal={payByVoucherModal}
        onClose={onPayByVoucherClose}
        modalSize="large"
      >
        <AddVoucherPayment
          onCancel={onPayByVoucherClose}
          clientId={client.clientId}
        />
      </Modal>

      <Modal
        modal={payByOneVoucherModal}
        onClose={onPayByOneVoucherClose}
        modalSize="large"
      >
        <AddOneVoucherPayment
          onCancel={onPayByOneVoucherClose}
          clientId={client.clientId}
        />
      </Modal>

      <div className="display-page__details-wrapper">
        <div className="display-page__column-left">
          <div className="display-page__intro">{paragraph}</div>
          <div className="display-page__details">
            <div className="display-page__details-row">
              <div className="display-page__details-key">Credit Available</div>
              <div className="display-page__details-value">
                {formatCurrencyInRands(client.balance)}
              </div>
            </div>
            <div className="display-page__details-row">
              <div className="display-page__details-key">BSG Available</div>
              <div className="display-page__details-value">
                {client?.bsgClaimed < 2
                  ? formatCurrencyInRands(bsgAvailable)
                  : "Already Claimed"}
              </div>
            </div>

            <div className="display-page__details-row">
              <div className="display-page__details-key">Package</div>
              <div className="display-page__details-value">
                <SingleSelect
                  options={packageOptions}
                  field={{
                    value: selectedPackage,
                    onChange: onPackageChange,
                  }}
                />
              </div>
            </div>

            <div className="display-page__details-row">
              <div className="display-page__details-key">
                Package Price for {packageDatils?.billingDays ?? 0} days
              </div>
              <div className="display-page__details-value">
                {formatCurrencyInRands(packageDatils?.price)}
              </div>
            </div>

            <div className="display-page__details-row">
              <div className="display-page__details-key">Amount Due</div>
              <div className="display-page__details-value">
                {formatCurrencyInRands(requiredBalance)} for {daysProvided} day
                {daysProvided !== 1 ? "s" : ""}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="display-page__footer-buttons">
        <Button
          onClick={() => setPayByBankModal(true)}
          name={"Pay By Bank"}
          className={"button--primary"}
        />
        <Button
          onClick={() => setPayByVoucherModal(true)}
          name={"Pay by OTT Voucher"}
          className={"button--primary"}
        />
        <Button
          onClick={() => setPayByOneVoucherModal(true)}
          name={"Pay by One Voucher"}
          className={"button--primary"}
        />
        <a href="#" className="zonke-terms" onClick={onTermsAndConditions}>
          Zonke Energy T&apos;s & C&apos;s
        </a>
      </div>
    </div>
  );
};

export default TopUp;
